import { supabase } from '@/integrations/supabase/client';
import { ApiService } from '@/services/api/ApiService';
import { TokenService } from '@/services/auth/TokenService';
import { ActivationPackService } from '@/services/database/ActivationPackService';
import { ImpersonationService } from '@/services/users/ImpersonationService';
import { SetupAnalysisResult } from '@/types/agent';
import type { Agent } from '@/types/agent/agent';
import { ActivationFunnelService } from '@/services/analytics/ActivationFunnelService';
import { AgentCacheManager } from './AgentCacheManager';
import { AgentMapper } from './AgentMapper';

/**
 * Activation funnel — a new Genie's public page is the first channel that can
 * reach it, so a Genie that came back with a `url_name` has a chat channel
 * connected. Emitted here rather than in `/welcome` so every creation route
 * (setup wizard, persona quick-create, clone) is covered, not just one.
 */
const recordChatChannel = (agent: { id?: string | null; url_name?: string | null; page?: { url_name?: string | null } | null } | null) => {
  if (!agent?.id) return;
  const urlName = agent.url_name ?? agent.page?.url_name ?? null;
  if (!urlName) return;
  ActivationFunnelService.channelConnected('chat', 'provisioned', agent.id);
};

/**
 * Kick the autonomous Genie Page pack (FAQ + overview, published day one) the
 * moment a Genie exists. The knowledge-base chokepoints (attach/detach/replace/
 * update) already request this server-side, but a Genie created with its
 * documents in the initial config never crosses those chokepoints — without
 * this kick its page stays empty until the knowledge changes. auto-provision
 * is serialised and debounced per Genie, so an extra kick is always safe.
 */
/**
 * handle-agent wraps ElevenLabs' 400 file_too_large response in a generic
 * "failed to sync with ElevenLabs" message. Extract the current/max
 * character counts so the UI can show a precise, actionable error instead
 * of the raw nested error text.
 */
const knowledgeBaseTooLargeMessage = (rawMessage: string): string | null => {
  if (!rawMessage.includes('file_too_large')) return null;

  const sizeMatch = rawMessage.match(/Current knowledge base size:\s*([\d.]+),\s*maximum:\s*([\d.]+)/);
  if (!sizeMatch) {
    return 'The knowledge base is too large for the selected model. Remove some files or choose a different model.';
  }

  const current = Number(sizeMatch[1]);
  const max = Number(sizeMatch[2]);
  const overBy = current - max;
  return `The knowledge base is too large for the selected model (${current.toLocaleString()} / ${max.toLocaleString()} characters, ${overBy.toLocaleString()} over). Remove some files or choose a different model.`;
};

const kickPageSynthesis = (agentId?: string | null): void => {
  if (!agentId) return;
  void ApiService.invoke({
    resource: 'genie-documents',
    action: 'auto-provision',
    data: { agentId },
  }).catch((error) => {
    console.warn('Genie Page synthesis kick failed (page will fill on next knowledge change):', error);
  });
};

/** What a clone takes from the original. Files are copied, never shared; the original's mailbox is never reused. */
export interface CloneOptions {
  copyKnowledgeBase?: boolean;
  copyResources?: boolean;
  provisionMailbox?: boolean;
}

/** What the clone actually ended up with; `resources` is null when Resources were not copied. */
export interface CloneReport {
  knowledgeDocuments: { copied: number; failed: number };
  resources: { links: number; documents: number; failedDocuments: number } | null;
}

export interface CloneResult {
  agent: Agent | null;
  report: CloneReport | null;
}

export class AgentProvisionService {
  public static async create(analysis: SetupAnalysisResult) {
    const token = await TokenService.getAccessToken();

    if (!token) {
      throw new Error("Authentication required for genie creation");
    }

    if (!analysis.voiceId) {
      console.warn("No voiceId set on analysis before provisioning — voice must be selected from preselected list during setup.");
    }

    const impersonatedUserId = ImpersonationService.getImpersonatedUserId();

    if (impersonatedUserId) {
      analysis.impersonatedUserId = impersonatedUserId;
    }

    console.log("Creating agent with analysis:", {
      genieName: analysis.genieName,
      voiceId: analysis.voiceId,
      maxDuration: analysis.conversationSettings?.maxDuration,
    });

    const { data, error } = await supabase.functions.invoke("handle-agent", {
      body: {
        action: "create",
        data: analysis,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (error) {
      // Supabase functions client returns data: null for non-2xx responses.
      // The actual error body is in error.context (raw Response object).
      let edgeFnError = "Failed to create agent";
      try {
        if (error.context && typeof error.context.json === "function") {
          const errorBody = await error.context.json();
          edgeFnError = errorBody?.error || errorBody?.message || error.message || edgeFnError;
        } else {
          edgeFnError = data?.error || data?.message || error.message || edgeFnError;
        }
      } catch {
        edgeFnError = error.message || edgeFnError;
      }
      console.error("Agent creation edge function error:", edgeFnError);
      throw new Error(edgeFnError);
    }

    if (!data?.success) {
      const failureReason = data?.message || data?.error || "Failed to create agent";
      console.error("Agent creation failed:", { data, reason: failureReason });
      throw new Error(failureReason);
    }

    AgentCacheManager.invalidateCache();
    const createdAgent = AgentMapper.map(data.data);

    if (createdAgent?.id) {
      await ActivationPackService.createDefaultPackForGenie(createdAgent.id, createdAgent.name);
      recordChatChannel(createdAgent);
      kickPageSynthesis(createdAgent.id);
    }

    return createdAgent;
  }

  public static async createFromPersona(
    personaId: string,
    options: {
      businessName?: string;
      customName?: string;
      impersonatedUserId?: string;
    } = {},
  ) {
    const token = await TokenService.getAccessToken();
    if (!token) {
      throw new Error("Authentication required for genie creation");
    }

    const { data, error } = await supabase.functions.invoke("handle-agent", {
      body: {
        action: "persona-create",
        personaId,
        businessName: options.businessName,
        customName: options.customName,
        impersonatedUserId:
          options.impersonatedUserId || ImpersonationService.getImpersonatedUserId(),
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (error || !data?.success) {
      throw new Error(data?.error || data?.message || error?.message || "Failed to create genie");
    }

    AgentCacheManager.invalidateCache();
    const createdAgent = AgentMapper.map(data.data);
    if (createdAgent?.id) {
      await ActivationPackService.createDefaultPackForGenie(createdAgent.id, createdAgent.name);
      recordChatChannel(createdAgent);
      kickPageSynthesis(createdAgent.id);
    }
    return createdAgent;
  }

  public static async update(agent: Agent, updates: Partial<Agent> & Record<string, unknown>) {
    console.log("Updating agent via function:", agent.id, updates);

    if (Object.keys(updates).length === 0) {
      return AgentMapper.map(agent);
    }

    const token = await TokenService.getAccessToken();

    if (!token) {
      throw new Error("Authentication required for genie update");
    }

    // const impersonatedUserId = ImpersonationService.getImpersonatedUserId();

    const { data, error } = await supabase.functions.invoke("handle-agent", {
      body: {
        action: "update",
        agentId: agent.id,
        updates,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (error) {
      // Supabase functions client returns data: null for non-2xx responses.
      // The actual error body is in error.context (raw Response object).
      let edgeFnError = "Failed to update agent";
      try {
        if (error.context && typeof error.context.json === "function") {
          const errorBody = await error.context.json();
          edgeFnError = errorBody?.error || errorBody?.message || error.message || edgeFnError;
        } else {
          edgeFnError = data?.error || data?.message || error.message || edgeFnError;
        }
      } catch {
        edgeFnError = error.message || edgeFnError;
      }
      console.error("Agent update edge function error:", edgeFnError);
      throw new Error(knowledgeBaseTooLargeMessage(edgeFnError) ?? edgeFnError);
    }

    if (!data?.success) {
      throw new Error(data?.message || "Failed to update agent");
    }

    AgentCacheManager.invalidateCache();

    return AgentMapper.map(data.data);
  }

  public static async delete(agentId: string | null, dbAgentId: string): Promise<void> {
    console.log("Deleting agent with\nEL ID:", agentId, "\nDB ID:", dbAgentId);

    try {
      const token = await TokenService.getAccessToken();

      if (!token) {
        throw new Error("Authentication required for agent deletion");
      }

      const { data, error } = await supabase.functions.invoke("handle-agent", {
        body: {
          action: "delete",
          agentId: dbAgentId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to delete agent");
      }

      if (!data?.success) {
        throw new Error(data?.message || "Failed to delete agent");
      }

      // Invalidate cache
      AgentCacheManager.invalidateCache();

      console.log("Agent deleted successfully.\nEL ID:", agentId, "\nDB ID:", dbAgentId);
    } catch (error) {
      console.error("Error deleting agent:", error);
      throw error;
    }
  }

  public static async duplicateAgent(
    agentId: string,
    customName?: string,
    promptReplacements?: { from: string; to: string }[],
    userId?: string,
    options?: CloneOptions,
  ): Promise<CloneResult> {
    console.log("Duplicating agent:", { agentId, customName, userId });

    const token = await TokenService.getAccessToken();
    if (!token) {
      throw new Error("Authentication required for agent duplication");
    }

    const { data, error } = await supabase.functions.invoke("api", {
      body: {
        resource: "genies",
        action: "clone",
        id: agentId,
        data: {
          ...(customName && { name: customName }),
          ...(userId && { userId }),
          ...(promptReplacements?.length && { promptReplacements }),
          ...options,
        },
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (error) {
      // Non-2xx: data is null and the body ({ error: { message } }) lives on error.context.
      let errorMsg = error.message || "Failed to clone agent";
      try {
        const body = error.context && typeof error.context.json === "function" ? await error.context.json() : data;
        const bodyError = body?.error;
        errorMsg = (typeof bodyError === "string" ? bodyError : bodyError?.message) || body?.message || errorMsg;
      } catch {
        // keep error.message
      }
      console.error("Agent cloning error:", { error: errorMsg, data });
      throw new Error(errorMsg);
    }

    if (!data?.success) {
      const failureReason = data?.message || data?.error || "Failed to clone agent";
      console.error("Agent cloning failed:", { data, reason: failureReason });
      throw new Error(failureReason);
    }

    const clonedAgent = data.agent;

    if (clonedAgent?.id) {
      await ActivationPackService.createDefaultPackForGenie(clonedAgent.id, clonedAgent.name);
      recordChatChannel(clonedAgent);
      kickPageSynthesis(clonedAgent.id);
    }

    return { agent: clonedAgent ?? null, report: data.report ?? null };
  }
}
